import { useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";

const pairs = [
  {
    label: "ऑफिस स्पेस",
    en: "OFFICE SPACE",
    before:
      "https://images.unsplash.com/photo-1722901154339-5991404e3262?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    after:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?fm=jpg&q=80&w=1200&auto=format&fit=crop",
  },
  {
    label: "सोफा व असबाब",
    en: "SOFA & UPHOLSTERY",
    before:
      "https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    after:
      "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?fm=jpg&q=80&w=1200&auto=format&fit=crop",
  },
  {
    label: "फ्लोर",
    en: "FLOOR",
    before:
      "https://images.unsplash.com/photo-1764109807172-acec712df813?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    after:
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?fm=jpg&q=80&w=1200&auto=format&fit=crop",
  },
];

const CompareSlider = ({ pair, index }) => {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const update = (clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setPos(Math.min(95, Math.max(5, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <Reveal delay={index * 0.1}>
      <div
        ref={ref}
        data-testid={`compare-slider-${index}`}
        className="relative aspect-[4/3] overflow-hidden border border-white/10 cursor-ew-resize select-none touch-none"
        onPointerDown={(e) => {
          dragging.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          update(e.clientX);
        }}
        onPointerMove={(e) => {
          if (dragging.current) update(e.clientX);
        }}
        onPointerUp={() => {
          dragging.current = false;
        }}
        onPointerCancel={() => {
          dragging.current = false;
        }}
      >
        <img
          src={pair.after}
          alt={`${pair.en} — after cleaning`}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <img
          src={pair.before}
          alt={`${pair.en} — before cleaning`}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        />
        <span className="absolute top-3 left-3 bg-[#0C1B24]/80 backdrop-blur px-3 py-1 font-spacemono text-[10px] tracking-[0.25em] text-white uppercase">
          पहले · Before
        </span>
        <span className="absolute top-3 right-3 bg-[#00F0FF] px-3 py-1 font-spacemono text-[10px] tracking-[0.25em] text-[#0C1B24] uppercase font-bold">
          बाद · After
        </span>
        <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
          <div className="absolute inset-y-0 -left-px w-0.5 bg-[#00F0FF]" />
          <div
            data-testid={`compare-handle-${index}`}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 bg-[#00F0FF] rounded-full flex items-center justify-center"
          >
            <MoveHorizontal className="w-5 h-5 text-[#0C1B24]" />
          </div>
        </div>
      </div>
      <div className="mt-4 flex items-baseline justify-between">
        <h3 className="font-khand font-semibold text-2xl">{pair.label}</h3>
        <span className="font-spacemono text-[10px] tracking-[0.3em] text-slate-500 uppercase">{pair.en}</span>
      </div>
    </Reveal>
  );
};

export const BeforeAfter = () => (
  <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32 border-t border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <SectionLabel>[ 04 — पहले / बाद · BEFORE-AFTER ]</SectionLabel>
          <h2 className="mt-4 font-khand font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight">
            फर्क़ <span className="text-[#00F0FF]">खुद देखिए</span>
          </h2>
        </div>
        <p className="max-w-sm text-sm md:text-base text-slate-400 font-mukta">
          स्लाइडर को दाएं-बाएं घसीटकर सफाई से पहले और बाद का फर्क़ देखें।
        </p>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {pairs.map((p, i) => (
          <CompareSlider key={p.en} pair={p} index={i} />
        ))}
      </div>
    </div>
  </section>
);
